import React from "react";
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Footer from "@/components/Footer";
import PrincipalDentist from "@/components/PrincipalDentist";
import Header from "@/components/Header";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { BOOKING_URL, LINK_ATTRIBUTES } from "@/lib/config";
import {
    Users,
    Phone,
    ArrowRight,
    Smile,
    Award,
    GraduationCap,
    Heart,
    Shield,
    Star
} from "lucide-react";


const OurTeamPage = () => {
    useScrollAnimation();

    const teamMembers = [
        {
            name: "Dr Pinar Geyik BDS",
            role: "Associate Dentist",
            registrationNumber: "DEN0002905804",
            image: "/Dr%20Pinar.svg",
            description: "Dr Pinar is a caring, patient-centred general dentist in Broadmeadows. She trained at La Trobe University, completed her graduate year with Monash Health, and gained valuable experience across rural Victorian communities. She enjoys all aspects of general and preventative dentistry with a strong interest in cosmetic dentistry and minor oral surgery. Dr Pinar is known for her calm, gentle approach and takes the time to explain treatment options clearly. She also provides care in English and Turkish."
        },
        {
            name: "Dr Javed Mahmud (AKA Dr Pavel)",
            role: "Senior Dentist",
            registrationNumber: "DEN0001774450",
            image: "/Dr%20Pavel.svg",
            description: "Dr Javed Mahmud (Dr Pavel) is a highly skilled and compassionate general dentist with a strong clinical background in oral surgery and dental implant treatment. After completing dental training overseas, he gained Australian registration and has built extensive experience in complex procedures including surgical extractions, wisdom teeth removal, and implant crown placement. He has completed multiple diplomas and a fellowship in dental implantology and is committed to clear communication, evidence-based care, and supporting anxious patients through every step of treatment."
        }
    ];

    const teamValues = [
        {
            icon: Heart,
            title: "Kind & Gentle",
            description: "We are friendly and understanding. We help you feel calm and relaxed."
        },
        {
            icon: Shield,
            title: "Great Quality",
            description: "We use the best methods and keep learning new skills to help you better."
        },
        {
            icon: Star,
            title: "You Come First",
            description: "We listen to you, explain everything clearly, and make a plan that's right for you."
        }
    ];

    return (
        <div className="min-h-screen flex flex-col">
      <SEO title="Your dentists in Broadmeadows | Y3 Smiles Dental" description="Meet the dentists at Y3 Smiles Dental in Broadmeadows. Compassionate, modern care with a focus on family dentistry, cosmetic treatments, and gentle oral surgery." image="/og-dr-fatima.jpg" />
            <Header />
            <main className="flex-grow">
                {/* Hero Section */}
                <section className="bg-gradient-hero py-24">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto text-center">
                            <Users className="w-20 h-20 text-primary mx-auto mb-6" />
                            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-neutral-800 mb-6">
                                Your dentists in Broadmeadows
                            </h1>
                            <p className="text-xl text-neutral-800 mb-8 leading-relaxed">
                                Meet the dentists who take care of Broadmeadows families. We explain everything clearly, listen to your needs, and help you feel comfortable.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button variant="booking" size="xl" asChild className="group">
                                    <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                        <Phone className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                        BOOK APPOINTMENT
                                    </a>
                                </Button>
                                <Button variant="outline" size="xl" asChild>
                                    <Link to="/about/principal-dentist">
                                        <ArrowRight className="w-5 h-5 mr-2" />
                                        Meet Principal Dentist
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Team Values */}
                <section className="py-24 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                                    What Drives Our Team
                                </h2>
                                <p className="text-lg text-neutral-800 max-w-3xl mx-auto">
                                    Our shared values guide everything we do and ensure every patient receives the best possible care
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                                {teamValues.map((value, index) => {
                                    const IconComponent = value.icon;
                                    return (
                                        <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 border border-primary/5 hover:border-primary/20 fade-in-section" style={{ transitionDelay: `${index * 150}ms` }}>
                                            <CardContent className="p-8">
                                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                                                    <IconComponent className="w-8 h-8 text-primary" />
                                                </div>
                                                <h3 className="text-xl font-bold text-neutral-800 mb-4">
                                                    {value.title}
                                                </h3>
                                                <p className="text-neutral-800 leading-relaxed">
                                                    {value.description}
                                                </p>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Team Members */}
                <section className="py-24 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                                    Meet Our Professionals
                                </h2>
                                <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
                                    Our experienced team brings together clinical expertise, genuine care, and a commitment to your wellbeing
                                </p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                                {teamMembers.map((member, index) => (
                                    <Card key={index} className="group hover:shadow-xl transition-all duration-500 border border-primary/5 hover:border-primary/20 fade-in-section" style={{ transitionDelay: `${index * 200}ms` }}>
                                        <CardContent className="p-8">
                                            <div className="flex flex-col md:flex-row gap-6">
                                                {/* Photo */}
                                                <div className="md:w-48 flex-shrink-0">
                                                    <div className="relative">
                                                        <div className="w-full aspect-[3/4] rounded-2xl overflow-hidden shadow-lg group-hover:shadow-xl transition-shadow">
                                                            <img
                                                                src={member.image}
                                                                alt={member.name}
                                                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                                                            />
                                                        </div>
                                                        <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center shadow-lg">
                                                            <Smile className="w-6 h-6 text-white" />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1">
                                                    <h3 className="text-2xl font-bold text-neutral-800 mb-2 group-hover:text-primary transition-colors">
                                                        {member.name}
                                                    </h3>

                                                    {member.registrationNumber && (
                                                        <p className="text-xs text-neutral-500 mb-3">
                                                            Registration: {member.registrationNumber}
                                                        </p>
                                                    )}

                                                    <div className="inline-flex items-center bg-gradient-to-r from-primary/10 to-secondary/10 px-4 py-2 rounded-full mb-4">
                                                        <span className="text-primary font-semibold text-sm">
                                                            {member.role}
                                                        </span>
                                                    </div>

                                                    <p className="text-neutral-800 leading-relaxed mb-6">
                                                        {member.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                            <div className="text-center mt-16">
                                <div className="bg-gradient-to-r from-secondary/10 to-primary/10 p-8 rounded-2xl border border-secondary/20 inline-block">
                                    <Award className="w-12 h-12 text-secondary mx-auto mb-4" />
                                    <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                                        Expanding Our Team
                                    </h3>
                                    <p className="text-neutral-800 mb-4">
                                        We're always looking for passionate dental professionals to join our team.
                                        Explore career opportunities with us!
                                    </p>
                                    <Button variant="outline" asChild>
                                        <Link to="/about/join-our-team">
                                            View Career Opportunities
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </Link>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-24 bg-background">
                    <div className="container mx-auto px-6 text-center">
                        <div className="max-w-3xl mx-auto">
                            <Phone className="w-16 h-16 text-primary mx-auto mb-6" />
                            <h2 className="text-4xl font-bold text-neutral-800 mb-6">
                                Experience Our Team's Expert Care
                            </h2>
                            <p className="text-lg text-neutral-800 mb-8">
                                Our dedicated professionals are ready to help you achieve optimal oral health.
                                Book your appointment and experience the Y3 Smiles difference.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button variant="booking" size="xl" asChild className="group">
                                    <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                        <Phone className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                        BOOK APPOINTMENT
                                    </a>
                                </Button>
                                <Button variant="outline" size="xl" asChild>
                                    <Link to="/treatments">
                                        <ArrowRight className="w-5 h-5 mr-2" />
                                        View Our Services
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ADA Membership */}
                <section className="py-10 bg-neutral-50">
                    <div className="container mx-auto px-6 text-center">
                        <a 
                            href="https://ada.org.au/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-block hover:opacity-80 transition-opacity"
                        >
                            <img
                                src="https://imgs.search.brave.com/8v9wd8wTiBlgDKX8TE6bUFOHhnbfVZOUyUIe9XAyriw/rs:fit:500:0:1:0/g:ce/aHR0cHM6Ly93d3cu/aGlnaGVybG9naWMu/Y29tL3dwLWNvbnRl/bnQvdXBsb2Fkcy8y/MDI0LzA1L3RodW1i/bmFpbF9BREFfbG9n/b19ibHVlLnBuZw"
                                alt="Australian Dental Association logo"
                                className="mx-auto h-16 w-auto"
                                loading="lazy"
                                decoding="async"
                            />
                        </a>
                        <p className="mt-4 text-sm text-neutral-700">
                            Member of the <a href="https://ada.org.au/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Australian Dental Association</a>
                        </p>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default OurTeamPage;